import type { VariablesContextValue } from '@core/context';
import type { ReadChangelogType } from '@readme/api-contracts/schemas/changelog';
import type { CustomPageReadType } from '@readme/api-contracts/schemas/custompage';
import type { RecipeReadType } from '@readme/api-contracts/schemas/recipe';
import type { ReadGuideType } from '@readme/api/mappings/page/guide/types';
import type { ReadReferenceType } from '@readme/api/mappings/page/reference/types';

import { renderMdxPlain } from '@readme/backend/iso';

/**
 * Fetches the description value for the `<meta name=description>`
 * with the following priority:
 * - page.metadata.description (from the modal)
 * - page.content.excerpt
 * - extracted description from page.content.body
 *
 * @note This logic mirrors the `getDescription` function in our Hub express app.
 * If any changes are made to this function you must also make updates there.
 * @link /packages/hub/lib/createMetadata.ts
 */
export default function getMetaDescription(
  page: (CustomPageReadType | ReadChangelogType | ReadGuideType | ReadReferenceType | RecipeReadType)['data'] | null,
  variables: VariablesContextValue,
) {
  if (page && 'metadata' in page && page.metadata?.description) {
    return page.metadata.description;
  }

  const mdxOpts = { truncationLength: 300 };

  if (page && 'content' in page && 'excerpt' in page.content && page.content.excerpt) {
    return renderMdxPlain(page.content.excerpt, variables, mdxOpts);
  }

  if (page && 'content' in page && 'body' in page.content && page.content.body) {
    return renderMdxPlain(page.content.body, variables, mdxOpts);
  }

  return '';
}
